import { createContext, useContext, useState } from "react";
import apiClient from "../api/clients";
import { ENDPOINTS } from "../api/endpoints";
import dayjs from "dayjs";

const JobCreationContext = createContext();

const normalizeCompensation = (comp) => {
    if (!comp) return {};
    return Object.fromEntries(
        Object.entries(comp).map(([k, v]) => [k, v === "" || v === null ? "N/A" : v])
    );
};

export const JobCreationProvider = ({ children }) => {
    const [jobData, setJobData] = useState({
        id: null,
        basics: {
            set_expiration: false,
            useNAExpiration: false,
        },
        compensation: normalizeCompensation({}),
        description: "",
        media: {},
    });

    const updateJobData = (section, data) => {
        setJobData((prev) => {
            let updated;

            if (section === "compensation") {
                const normalized = normalizeCompensation(data);
                updated = normalized;
            } else if (section === "basics" || section === "media") {
                updated = { ...prev[section], ...data };
            } else {
                updated = data;
            }

            return { ...prev, [section]: updated };
        });
    };

    const loadJobData = (job) => {
        const basics = job.basics || { set_expiration: false };
        let expirationValue = null;
        let useNAExpiration = false;

        if (basics.expirationDate === "N/A") {
            expirationValue = null;
            useNAExpiration = true;
        } else if (basics.expirationDate && dayjs(basics.expirationDate, "YYYY-MM-DD", true).isValid()) {
            expirationValue = dayjs(basics.expirationDate, "YYYY-MM-DD");
        }

        setJobData({
            id: job.id || null,
            basics: {
                ...basics,
                expirationDate: expirationValue,
                useNAExpiration,
            },
            compensation: normalizeCompensation(job.compensation),
            description: job.description || "",
            media: job.media || {},
        });
    };

    const submitJobData = async () => {
        try {
            const { id, basics, compensation, description, media } = jobData;
            const formData = new FormData();
            const userId = localStorage.getItem("jobUserId");
            if (!userId) throw new Error("Missing user ID");

            formData.append("user_id", userId);
            if (id) formData.append("id", id);

            formData.append("title", basics.title || "");
            formData.append("location", basics.location || "");
            formData.append("job_type", basics.job_type || "");
            formData.append("category", basics.category || "");

            if (compensation) {
                if (compensation.minimum) formData.append("minimum", compensation.minimum);
                if (compensation.maximum) formData.append("maximum", compensation.maximum);
                if (compensation.currency) formData.append("currency", compensation.currency);
                if (compensation.salary_date) formData.append("salary_date", compensation.salary_date);
            }

            formData.append("description", description || "No Description Provided");

            if (basics.useNAExpiration) {
                formData.append("expiration_date", "N/A");
            } else if (basics.expirationDate) {
                formData.append("expiration_date", dayjs(basics.expirationDate).format("YYYY-MM-DD"));
            }

            if (media.image && typeof media.image !== "string") {
                formData.append("image", media.image);
            }

            const endpoint = ENDPOINTS.JOBS.JOB_POSTS();
            const method = id ? "patch" : "post";

            const response = await apiClient[method](endpoint, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            return response.data;
        } catch (error) {
            console.error("Failed to create job:", error);
            throw error;
        }
    };

    return (
        <JobCreationContext.Provider value={{ jobData, updateJobData, loadJobData, submitJobData }}>
            {children}
        </JobCreationContext.Provider>
    );
};

export const useJobCreation = () => useContext(JobCreationContext);